import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MaskedTextBoxComponent } from '@progress/kendo-angular-inputs';
import { SelectRoleComponent } from 'src/app/shared/select-role/select-role.component';
import { MessageHandlerService } from '../utility/message-handler.service';
import { FhirService } from '../service/fhir.service';
import { ChangePasswordComponent } from 'src/app/shared/change-password/change-password.component';
import { DocumentService } from '../service/document.service';
import { SettingService } from '../service/setting.service';
import { SelectOrganizationComponent } from 'src/app/shared/select-organization/select-organization.component';

export interface TwoStepVerificationCodeSend
{
  username: string;
  email: string;
  sms: string;
}

/**
 * Time-based one-time password validation component
 */
@Component({
  standalone: false,
  selector: 'app-totp',
  templateUrl: './totp.component.html',
  styleUrls: ['./totp.component.scss']
})
export class TotpComponent implements OnInit, AfterViewInit
{
  @ViewChild('maskedinput') public maskInput: MaskedTextBoxComponent;
  @ViewChild('selectRole') public selectRole: SelectRoleComponent;
  @ViewChild('selectOrganization') public selectOrganization: SelectOrganizationComponent;
  @ViewChild('changePassword') public changePassword: ChangePasswordComponent;

  public formAuthenticator: any;
  public formAlternativeAuth: any;
  public mask = '000000';
  public validating = false;
  public invalidCode = false;
  public successAuthentication = false;
  public differentAuthMethodAllowed = false;
  public showQRCode = false;
  public qrCodeVisible = false;
  public qrCodeImageUrl = null;
  public totpState = 0;       // 0 = Authenticator App Code, 1 = Send Alternate Code , 2 = Enter Alternate Code
  public totpSentCodeCount = 0;
  public brandDocumentId = null;
  public brandImageLoaded = false;
  public hasBrandImage = false;
  public brandImageUrl: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private fhirService: FhirService,
    private messageHandlerService: MessageHandlerService,
    private documentService: DocumentService,
    private settingService: SettingService,
    private authenticationService: AuthenticationService)
  {
    this.formAuthenticator = this.formBuilder.group({
      verificationCode: [{ value: null, disabled: false }],
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.brandDocumentId = sessionSetting.brandLogoDocumentId;
        this.getBrandPhotoFromService();
      }
    });
  }

  public get verificationCode(): UntypedFormControl
  {
    return this.formAuthenticator.get('verificationCode');
  }

  public get methodRequest(): UntypedFormControl
  {
    return this.formAlternativeAuth.get('methodRequest');
  }

  public get sms(): UntypedFormControl
  {
    return this.formAlternativeAuth.get('sms');
  }

  public get email(): UntypedFormControl
  {
    return this.formAlternativeAuth.get('email');
  }

  public ngOnInit(): void
  {
    this.totpState = 0;
    this.totpSentCodeCount = 0;
    this.showQRCode = this.authenticationService.totpQRCode !== null;
    this.qrCodeImageUrl = this.showQRCode ? this.authenticationService.totpQRCode.qrCodeImage : null;

    // In case two factor storage is missing, we redirect to login automatically.
    if (!this.authenticationService.totpUserName || !this.authenticationService.totpToken)
    {
      this.cancel();
    }
    else
    {
      this.differentAuthMethodAllowed = this.authenticationService.totpDifferentAuthMethodAllowed &&
        (this.authenticationService.totpMobile !== '' || this.authenticationService.totpEmail !== '');
    }
  }

  public ngAfterViewInit(): void
  {
    this.maskInput.focus();
  }

  public cancel(): void
  {
    this.authenticationService.logout();
  }

  public validateKeyDown(event: KeyboardEvent): void
  {
    if (this.totpState === 0 || this.totpState === 2)
    {
      if (this.validating || this.successAuthentication) { return; }
      this.invalidCode = false;
      if (event && event.key === 'Enter' && this.formAuthenticator.valid)
      {
        event.preventDefault();
        this.validate();
      }
    }
  }

  /**
   * This method is sending the code for the current username by sms or email depending on the selected option.
   */
  public sendCode(): void
  {
    const codeSend: TwoStepVerificationCodeSend = { username: this.authenticationService.totpUserName, sms: null, email: null };
    switch (this.methodRequest.value)
    {
      case 'sms':
        codeSend.sms = this.sms.value;
        break;
      case 'email':
        codeSend.email = this.email.value;
        break;
    }
    this.authenticationService.sendCode(codeSend)
      .subscribe(
        {
          next: () =>
          {
            this.messageHandlerService.displaySuccess('TWO-STEP-VERIFICATION-CODE-SENT');
            this.totpSentCodeCount += 1;
          },
          error: (errorResponse: any) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
  }

  /**
   * Enter code event clicked
   */
  public enterCode(): void
  {
    this.verificationCode.setValue(null);
    this.formAuthenticator.reset();
    this.totpState = 2;
    setTimeout(() =>
    {
      this.maskInput.focus();
    });
  }

  /**
   * Validate the QR Code or alternative two step verification code.
   */
  public validate(): void
  {
    this.validating = true;
    this.invalidCode = false;
    this.successAuthentication = false;

    const verification = {
      method: this.totpState === 0 ? 'authenticator' : 'token',
      code: this.verificationCode.value,
      username: this.authenticationService.totpUserName,
      fhirIssuerId: this.fhirService?.issuer ? this.fhirService.issuer.id : null,
      fhirProfileId: this.fhirService?.oAuthToken?.user ? decodeURIComponent(this.fhirService.oAuthToken.user) : null
    };

    this.authenticationService.verifyCode(verification).subscribe({
      next: (data) =>
      {
        this.authenticationService.completeLoginProcess(data, this.authenticationService.totpReturnUrl, this.selectRole, this.selectOrganization, this.changePassword, this.authenticationService.totFHIR);
        this.validating = false;
        this.successAuthentication = true;
      },
      error: () =>
      {
        this.validating = false;
        this.invalidCode = true;
      }
    });
  }

  /**
   * Build the alternative authentication code form.
   */
  public performAlternativeAuth(): void
  {
    this.formAlternativeAuth = this.formBuilder.group({
      methodRequest: [{ value: null, disabled: false }, { validators: [Validators.required] }],
      sms: [{ value: this.authenticationService.totpMobile ? this.authenticationService.totpMobile : null, disabled: false }],
      email: [{ value: this.authenticationService.totpEmail ? this.authenticationService.totpEmail : null, disabled: false }],
    });
    this.totpState = 1;
  }

  /**
   * Toggle QR Code visibility
   */
  public toggleQRCode(): void
  {
    this.qrCodeVisible = !this.qrCodeVisible;
  }

  private getBrandPhotoFromService()
  {
    this.brandImageLoaded = false;
    this.hasBrandImage = false;
    if (this.brandDocumentId !== null && this.brandDocumentId > 0)
    {
      this.documentService.retrieveBrandImageDocument().subscribe({
        next: (data) =>
        {
          this.hasBrandImage = data.content !== null;
          if (this.hasBrandImage)
          {
            const base64 = data.content;
            this.brandImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          this.brandImageLoaded = true;
        }
      });
    }
  }
}
